import React from 'react';
import PropTypes from 'prop-types';
import Translations from '../../../../../requests/apa/Translator';
import './_MatchDetailHeaderGeneral.scss';

const MatchDetailHeaderGeneral = ({ matchData, teamColors, statsData, liveActions, isLoadingActions }) => {
  let {
    homeTeam = {},
    awayTeam = {},
    homeScore = 0,
    awayScore = 0,
    competition,
    group,
    date,
    time,
    city,
    referee,
    venue,
    attendance,
    stage,
    homeGoals = [],
    awayGoals = [],
    matchProgress = {}
  } = matchData || {};

  const colors = {
    home: (teamColors && teamColors.home) || 'rgba(255,255,255,0.25)',
    away: (teamColors && teamColors.away) || 'rgba(255,255,255,0.25)',
  };

  homeScore = homeScore != null ? homeScore : 0;
  awayScore = awayScore != null ? awayScore : 0;

  const MONTH_EN_TO_ES = {
  JANUARY: 'ENERO', FEBRUARY: 'FEBRERO', MARCH: 'MARZO',
  APRIL: 'ABRIL', MAY: 'MAYO', JUNE: 'JUNIO',
  JULY: 'JULIO', AUGUST: 'AGOSTO', SEPTEMBER: 'SEPTIEMBRE',
  OCTOBER: 'OCTUBRE', NOVEMBER: 'NOVIEMBRE', DECEMBER: 'DICIEMBRE'
}

function localizeDate(dateStr) {
  if (!dateStr) return dateStr
  return dateStr.replace(
    /(?:DE\s+)?\b(JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)\b/gi,
    function (_, month) {
      return 'DE ' + (MONTH_EN_TO_ES[month.toUpperCase()] || month.toUpperCase())
    }
  )
}

const getActionIcon = (type) => {
  const icons = {
    'GOAL': '⚽',
    'YELLOW_CARD': '🟨',
    'RED_CARD': '🟥',
    'SUBSTITUTION': '🔄',
    'CORNER': '🚩',
    'PENALTY': '🎯',
    'VAR': '📺',
    'OFFSIDE': '🚫',
    'FOUL': '⚠️',
    'KICKOFF': '🏁',
    'HALFTIME': '⏸️',
    'FULLTIME': '🏁',
  };
  return icons[type] || '•';
};

const getActionClassName = (type) => {
  const baseClass = 'match-detail-header__action';
  const typeMap = {
    'GOAL': `${baseClass}--goal`,
    'YELLOW_CARD': `${baseClass}--yellow-card`,
    'RED_CARD': `${baseClass}--red-card`,
    'SUBSTITUTION': `${baseClass}--substitution`,
    'PENALTY': `${baseClass}--penalty`,
    'VAR': `${baseClass}--var`,
  };
    const modifier = typeMap[type];
   return modifier ? `${baseClass} ${modifier}` : baseClass;
};

const GoalItems = ({ goals = [], isAway = false }) => {
  if (!goals || !Array.isArray(goals) || goals.length === 0) {
    return null;
  }

  const itemClass = isAway ? 'match-detail-header__goal-item--away' : 'match-detail-header__goal-item--home';

  return (
    <div>
      {goals.slice(0, 3).map((goal, index) => (
        <div key={`${goal.player}-${index}`} className={`match-detail-header__goal-item ${itemClass}`}>
          <span className='match-detail-header__goal-player'>{goal.player}</span>
          <span className='match-detail-header__goal-minute'>{goal.minute}</span>
        </div>
      ))}
      {goals.length > 3 && (
        <div className={`match-detail-header__goal-item match-detail-header__goal-item--extra ${itemClass}`}>
          +{goals.length - 3} gol{goals.length - 3 > 1 ? 'es' : ''} más
        </div>
      )}
    </div>
  );
};

  const hasScore =
    homeScore !== null && homeScore !== undefined &&
    awayScore !== null && awayScore !== undefined;

    const minute = matchProgress && matchProgress.minute != null ? `${matchProgress.minute}'` : '';
    const second = matchProgress && matchProgress.second != null ? `${matchProgress.second}''` : '';
    const minuteLabel = `Minuto a minuto ${minute}`;

  const footerItems = [
    group || (stage ? stage : ''),
    competition || '',
    date && time ? '18 de JUNIO ' + ' - ' + time : (date || ''),
    city || '',
    referee ? 'Árbitro: Paul Tierney' : 'Árbitro: Paul Tierney',
    venue || '',
    attendance ? 'Asistencia' : 'Asistencia: 499999',
  ].filter(Boolean);

   const hasStats = Array.isArray(statsData) && statsData.length > 0;
  
  const safeDate = localizeDate(date);

  // Procesar acciones en vivo (mostrar solo las últimas 30)
  const displayActions = Array.isArray(liveActions) ? liveActions.slice(0, 30) : [];
  const hasLiveActions = displayActions.length > 0;

  return (
    <section
      className='mdh-general'
      style={{
        '--mdh-general-home-color': colors.home,
        '--mdh-general-away-color': colors.away,
      }}
    >
      {/* Badge equipo local */}
      <div className='mdh-general__badge mdh-general__badge--home'>
        {homeTeam.badgeUrl || homeTeam.flagUrl ? (
          <img
            src={homeTeam.badgeUrl || homeTeam.flagUrl}
            className='mdh-general__badge-img'
          />
        ) : (
          <span className='mdh-general__badge-initials'>{homeTeam.shortName || ''}</span>
        )}
      </div>

      {/* Badge equipo visitante */}
      <div className='mdh-general__badge mdh-general__badge--away'>
        {awayTeam.badgeUrl || awayTeam.flagUrl ? (
          <img
            src={awayTeam.badgeUrl || awayTeam.flagUrl}
            className='mdh-general__badge-img'
          />
        ) : (
          <span className='mdh-general__badge-initials'>{awayTeam.shortName || ''}</span>
        )}
      </div>

      {/* Cuerpo: equipo local | centro | equipo visitante */}
      <div className='mdh-general__body'>

        {/* Panel equipo local */}
        <div className='mdh-general__panel mdh-general__panel--home'>
          {homeTeam.flagUrl
            ? <img src={homeTeam.flagUrl} className='mdh-general__flag' />
            : null}
          <span className='mdh-general__vsep' aria-hidden='true' />
          <span className='mdh-general__team-name'>{homeTeam.name || ''}</span>
        </div>
        <div className='mdh-general__goals mdh-general__goals--home'>
          <GoalItems goals={homeGoals} isAway={false} />
        </div>

        {/* Centro: Minuto a minuto + marcador */}
        <div className='mdh-general__center'>
            {hasStats ? (<span className='mdh-general__minuto-label'>{minuteLabel}</span>) : null}
            {hasStats ? (<div className='mdh-general__score'>{homeScore + ' - ' + awayScore}</div>) : null}
            {!hasStats && (<div className='mdh-general__score_vs'>vs</div>)}
        </div>

        {/* Panel equipo visitante */}
        <div className='mdh-general__panel mdh-general__panel--away'>
          <span className='mdh-general__team-name'>{awayTeam.name || ''}</span>
          <span className='mdh-general__vsep' aria-hidden='true' />
          {awayTeam.flagUrl
            ? <img src={awayTeam.flagUrl} className='mdh-general__flag' />
            : null}
        </div>
        <div className='mdh-general__goals mdh-general__goals--away'>
          <GoalItems goals={awayGoals} isAway={true} />
        </div>

      </div>

      
      {/* Sección de eventos en vivo (Minuto a Minuto) */}
    {/* {(hasLiveActions || isLoadingActions) && (
        <div className='mdh-general__live-actions'>
          <div className='mdh-general__live-actions-header'>
            <h3 className='mdh-general__live-actions-title'>{Translations.get('eventodeportivo_texto_minuto_a_minuto')}</h3>
            {matchProgress && (matchProgress.minute || matchProgress.second) && (
              <span className='mdh-general__live-actions-time'>
                {matchProgress.minute || 0}' {matchProgress.second ? `${matchProgress.second}''` : ''}
              </span>
            )}
          </div>
          
          {isLoadingActions && displayActions.length === 0 ? (
            <div className='mdh-general__live-actions-loading'>
              {Translations.get('eventodeportivo_texto_cargando_acciones')}
            </div>
          ) : (
            <div className='mdh-general__live-actions-list'>
              {displayActions.map((action, index) => (
                <div 
                  key={action.id || `action-${index}`} 
                  className={getActionClassName(action.type)}
                >
                  <div className='match-detail-header__action-time'>
                    <span className='match-detail-header__action-icon'>
                      {getActionIcon(action.type)}
                    </span>
                    <span className='match-detail-header__action-minute'>
                      {action.minute || 0}'
                    </span>
                  </div>
                  
                  <div className='match-detail-header__action-content'>
                    <div className='match-detail-header__action-type'>
                      {action.typeLabel || action.type}
                    </div>
                    {action.description && (
                      <div className='match-detail-header__action-description'>
                        {action.description}
                      </div>
                    )}
                    {action.player && (
                      <div className='match-detail-header__action-player'>
                        {action.player}
                      </div>
                    )}
                    {action.team && (
                      <div className='match-detail-header__action-team'>
                        {action.team}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )} */}

      {/* Footer con metadata */}
      <div className='mdh-general__footer'>

                <div className='match-detail-header__footer'>
                  <div className='match-detail-header__meta'>
                    <span className='match-detail-header__meta-item'>{Translations.get(`eventodeportivo_texto_etapa_${stage}`)}</span>
                    <span className='match-detail-header__meta-item'> - </span>
                    <span className='match-detail-header__meta-item match-detail-header__meta-item--stacked'>
                      <span className='match-detail-header__meta-value'>{group}</span>
                      <span className='match-detail-header__meta-sep'>{Translations.get(`eventodeportivo_texto_separador`)}</span>
                      <span className='match-detail-header__meta-item'>{safeDate}</span>
                    </span>
                    
                    <span className='match-detail-header__meta-item'>-</span>
                    <span className='match-detail-header__meta-item'>{time}</span>
                    <span className='match-detail-header__meta-sep'>/</span>
                    <span className='match-detail-header__meta-item'>{city}</span>
                    <span className='match-detail-header__meta-sep'>{Translations.get(`eventodeportivo_texto_separador`)}</span>
                    <span className='match-detail-header__meta-item match-detail-header__meta-item--stacked'>
                      <span className='match-detail-header__meta-label'>Árbitro: </span>
                      <span className='match-detail-header__meta-value'>{referee}</span>
                    </span>
                    <span className='match-detail-header__meta-sep'>{Translations.get(`eventodeportivo_texto_separador`)}</span>
                    <span className='match-detail-header__meta-item match-detail-header__meta-item--stacked'>
                      <span className='match-detail-header__meta-value'>{venue}</span>
                    </span>
                    <span className='match-detail-header__meta-sep'>{Translations.get(`eventodeportivo_texto_separador`)}</span>
                    <span className='match-detail-header__meta-item'>Asistencia: {attendance}</span>
                  </div>
                </div>

      </div>

    </section>
  );
};

MatchDetailHeaderGeneral.propTypes = {
  matchData: PropTypes.shape({
    homeTeam: PropTypes.object,
    awayTeam: PropTypes.object,
    homeGoals: PropTypes.arrayOf(PropTypes.object),
    awayGoals: PropTypes.arrayOf(PropTypes.object),
    matchProgress: PropTypes.shape({
      minute: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      second: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  teamColors: PropTypes.shape({
    home: PropTypes.string,
    away: PropTypes.string,
  }),
  statsData: PropTypes.array,
  liveActions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    typeLabel: PropTypes.string,
    minute: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    player: PropTypes.string,
    team: PropTypes.string,
  })),
  isLoadingActions: PropTypes.bool,
};

MatchDetailHeaderGeneral.defaultProps = {
  matchData: {},
  teamColors: {},
  statsData: [],
  liveActions: [],
  isLoadingActions: false,
};

export default MatchDetailHeaderGeneral;


// WEBPACK FOOTER //
// ./src/containers/Mundial2026/FootballMatchDetail/Components/MatchDetailHeaderGeneral/MatchDetailHeaderGeneral.jsx